// src/components/SideMenu.tsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaHome,
  FaUsers,
  FaBook,
  FaChevronLeft,
  FaChevronRight,
  FaBars,
  FaEnvelopeOpenText,
  FaUserShield,
  FaChartBar,
  FaShieldAlt,
  FaBolt,
  FaClipboardCheck,
  FaHistory,
  FaServer,
  FaTag,
  FaWpforms,
  FaRocket,
  FaHandsHelping,
  FaBalanceScale,
  FaBoxes,
  FaFileAlt,
} from 'react-icons/fa';
import { useRoleAccess } from '@/contexts/RoleAccessContext';
import { SolutionKey } from '@/security/solutionAccess';
import '@/components/SideMenu.scss';

interface SideMenuProps {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const menuItems = [
  { name: 'Home', icon: <FaHome />, path: '/home' },
  { name: 'My Groups', icon: <FaUsers />, path: '/resources' },
  { name: 'Resource Health', icon: <FaBalanceScale />, path: '/resource-compliance' },
  { name: 'My Reviews', icon: <FaClipboardCheck />, path: '/reviews' },
  { name: 'Delegations', icon: <FaHandsHelping />, path: '/delegations', solution: 'delegations' as SolutionKey },
  { name: 'Workspaces', icon: <FaRocket />, path: '/workspaces', solution: 'workspaces' as SolutionKey },
  { name: 'Physical Records', icon: <FaBoxes />, path: '/physical-records', solution: 'physicalRecords' as SolutionKey },
  { name: 'My Activity Log', icon: <FaHistory />, path: '/my-activity-log' },
  { name: 'Help Articles', icon: <FaBook />, path: 'https://enmaxcorp.sharepoint.com/sites/ENMAXM365', external: true },
];

const adminMenuItems = [
  { name: 'Reviews', icon: <FaClipboardCheck />, path: '/admin/reviews', solution: 'reviews' as SolutionKey },
  { name: 'Email Approvals', icon: <FaEnvelopeOpenText />, path: '/admin/email-approval', solution: 'emailApprovals' as SolutionKey },
  { name: 'Role Management', icon: <FaUserShield />, path: '/admin/roles', solution: 'roleManagement' as SolutionKey },
  { name: 'Reports', icon: <FaChartBar />, path: '/admin/reports', solution: 'reports' as SolutionKey },
  { name: 'PIM', icon: <FaShieldAlt />, path: '/admin/pim', solution: 'pim' as SolutionKey },
  { name: 'Automated Elevations', icon: <FaBolt />, path: '/admin/pim/automated-elevations', solution: 'automatedElevations' as SolutionKey },
  { name: 'Groups Admin', icon: <FaServer />, path: '/admin/groups', solution: 'groupsAdmin' as SolutionKey },
  { name: 'OOB Status Board', icon: <FaShieldAlt />, path: '/admin/oob', solution: 'outOfBand' as SolutionKey },
  { name: 'Site.Selected Apps', icon: <FaShieldAlt />, path: '/admin/site-selected', solution: 'siteSelected' as SolutionKey },
  { name: 'SharePoint Inventory', icon: <FaFileAlt />, path: '/admin/sharepoint/inventory', solution: 'sharePointInventory' as SolutionKey },
  { name: 'Physical Records Admin', icon: <FaBoxes />, path: '/admin/physical-records', solution: 'physicalRecordsAdmin' as SolutionKey },
];

const SideMenu: React.FC<SideMenuProps> = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 992);
  const { loading, canAccessSolution } = useRoleAccess();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const canSeeEmployeeOffers = canAccessSolution('employeeOffers');
  const canSeeForms = canAccessSolution('forms');

  const isActive = (path: string) =>
    path === '/home' ? location.pathname === path : location.pathname.startsWith(path);

  const visibleAdminItems = adminMenuItems.filter(item => canAccessSolution(item.solution));
  const visibleMenuItems = menuItems.filter(item => !item.solution || canAccessSolution(item.solution));

  if (loading) {
    return null;
  }

  return (
    <div className={`sidebar-wrapper ${collapsed ? 'collapsed' : ''}`}>
      <div className={`side-menu ${collapsed ? 'collapsed' : ''}`}>
        <div className="menu-header">
          <span className="menu-title">{collapsed ? '' : 'Solutions'}</span>
          {isMobile ? (
            <button
              className="collapse-btn"
              onClick={() => setCollapsed(!collapsed)}
              aria-label="Toggle menu"
            >
              <FaBars />
            </button>
          ) : (
            <button
              className="collapse-btn"
              onClick={() => setCollapsed(!collapsed)}
              aria-label="Collapse sidebar"
            >
              {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
            </button>
          )}
        </div>

        <nav className="menu-items">
          {visibleMenuItems.map((item) =>
            item.external ? (
              <a
                key={item.name}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                className={`menu-item ${location.pathname === item.path ? 'active' : ''}`}
              >
                <span className="icon">{item.icon}</span>
                {!collapsed && <span className="text">{item.name}</span>}
              </a>
            ) : (
              <Link
                key={item.name}
                to={item.path}
                className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
              >
                <span className="icon">{item.icon}</span>
                {!collapsed && <span className="text">{item.name}</span>}
              </Link>
            )
          )}

          {/* Employee Offers — Solutions section */}
          {canSeeEmployeeOffers && (
            <Link
              to="/admin/employee-offers"
              className={`menu-item ${isActive('/admin/employee-offers') ? 'active' : ''}`}
            >
              <span className="icon"><FaTag /></span>
              {!collapsed && <span className="text">Employee Offers</span>}
            </Link>
          )}

          {/* Forms */}
          {canSeeForms && (
            <Link
              to="/admin/forms"
              className={`menu-item ${isActive('/admin/forms') ? 'active' : ''}`}
            >
              <span className="icon"><FaWpforms /></span>
              {!collapsed && <span className="text">Forms</span>}
            </Link>
          )}

          {/* Admin Section */}
          {visibleAdminItems.length > 0 && (
            <>
              <div className="menu-divider" />
              {!collapsed && (
                <Link to="/admin" className="menu-section-title menu-section-link">
                  Admin
                </Link>
              )}
              {visibleAdminItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
                >
                  <span className="icon">{item.icon}</span>
                  {!collapsed && <span className="text">{item.name}</span>}
                </Link>
              ))}
            </>
          )}
        </nav>
      </div>
    </div>
  );
};

export default SideMenu;